/**
 * Unlocking.
 *
 * Three tiers, offered in order of how little they cost: the session cache is
 * tried silently on mount, biometrics get a button, and the passphrase is the
 * one that always works.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/Button';
import { useVaultState } from '../hooks/useVaultState';
import { checkPassphraseShape } from '../vault/passphrase';
import { VaultKeyField } from '../components/VaultKeyField';
import { isWebAuthnAvailable } from '../vault/keks';
import * as vault from '../vault/vault';
import { useLanguage } from '../i18n/context';

export function Unlock() {
  const navigate = useNavigate();
  const state = useVaultState();
  const { t } = useLanguage();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void vault.unlockWithSession().then((ok) => {
      if (ok) navigate('/', { replace: true });
    });
  }, [navigate]);

  const shape = checkPassphraseShape(value);

  async function withPassphrase(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await vault.unlockWithPassphrase(value);
      navigate('/', { replace: true });
    } catch {
      setError(t.unlock.wrongKeyError);
    } finally {
      setBusy(false);
    }
  }

  async function withBiometric() {
    setBusy(true);
    setError(null);
    try {
      const ok = await vault.unlockWithBiometric();
      if (ok) navigate('/', { replace: true });
      else setError(t.unlock.biometricError);
    } catch {
      setError(t.unlock.biometricError);
    } finally {
      setBusy(false);
    }
  }

  if (!state.initialised) {
    return (
      <div className="mx-auto grid min-h-dvh w-full max-w-lg place-items-center px-5 text-center">
        <div>
          <h1 className="text-title text-ink-7">{t.unlock.noVaultTitle}</h1>
          <p className="text-body mt-2 mb-6 text-ink-5">
            {t.unlock.noVaultBody}
          </p>
          <div className="flex flex-col gap-2">
            <Button size="lg" onClick={() => navigate('/setup')}>
              {t.unlock.setupCta}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => navigate('/restore')}>
              {t.unlock.restoreCta}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-lg px-5 pt-[max(4rem,env(safe-area-inset-top))] pb-28">
      <h1 className="text-display text-ink-7">{t.unlock.title}</h1>
      <p className="text-body mt-2 mb-8 text-ink-5">
        {t.unlock.subtitle}
      </p>

      {state.biometricEnrolled && isWebAuthnAvailable() && (
        <Button
          variant="secondary"
          size="lg"
          className="mb-6 w-full"
          disabled={busy}
          onClick={withBiometric}
        >
          {t.unlock.biometricButton}
        </Button>
      )}

      <form onSubmit={withPassphrase} className="flex flex-col gap-3">
        <VaultKeyField
          label={t.setup.vaultKeyLabel}
          value={value}
          onChange={setValue}
          placeholder={t.unlock.keyPlaceholder}
          disabled={busy}
          autoFocus
        />

        {error && (
          <p role="alert" className="text-label text-action">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={!shape.ok || busy}>
          {busy ? t.unlock.unlocking : t.unlock.unlockButton}
        </Button>
      </form>

      <Button variant="quiet" className="mt-6 w-full" onClick={() => navigate('/restore')}>
        {t.unlock.restoreAlternative}
      </Button>
    </div>
  );
}
